import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { knowledgeBaseService, KnowledgeArticleSummary, KnowledgeCategory, KnowledgeSearchRequest, KnowledgeSearchSortBy } from '../services/knowledgeBaseService';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Loading } from '../components/Loading';
import { Badge } from '../components/Badge';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface KnowledgeBaseProps {}

export const KnowledgeBase: React.FC<KnowledgeBaseProps> = () => {
  useDocumentTitle('Knowledge Base | SupportDesk');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data state
  const [articles, setArticles] = useState<KnowledgeArticleSummary[]>([]);
  const [categories, setCategories] = useState<KnowledgeCategory[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<KnowledgeArticleSummary[]>([]);
  const [popularArticles, setPopularArticles] = useState<KnowledgeArticleSummary[]>([]);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sortBy, setSortBy] = useState<KnowledgeSearchSortBy>(KnowledgeSearchSortBy.Relevance);
  
  // UI state
  const [currentView, setCurrentView] = useState<'browse' | 'search'>('browse');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      const [
        articlesResponse,
        categoriesResponse,
        featuredResponse,
        popularResponse
      ] = await Promise.all([
        knowledgeBaseService.getArticles({ publicOnly: !user, page: 1, pageSize: 20 }),
        knowledgeBaseService.getCategories(!user),
        knowledgeBaseService.getFeaturedArticles(!user),
        knowledgeBaseService.getPopularArticles(10, !user)
      ]);

      setArticles(articlesResponse);
      setCategories(categoriesResponse);
      setFeaturedArticles(featuredResponse);
      setPopularArticles(popularResponse);
    } catch (err) {
      console.error('Error loading knowledge base data:', err);
      setError('Failed to load knowledge base. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setCurrentView('browse');
      return;
    }

    try {
      setSearchLoading(true);
      const searchRequest: KnowledgeSearchRequest = {
        query: searchQuery,
        categoryIds: selectedCategory ? [selectedCategory] : undefined,
        publicOnly: !user,
        sortBy,
        sortDescending: true,
        page: 1,
        pageSize: 20
      };

      const searchResult = await knowledgeBaseService.searchArticles(searchRequest);
      setArticles(searchResult.articles);
      setCurrentView('search');
    } catch (err) {
      console.error('Error searching articles:', err);
      setError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCategoryFilter = async (categoryId: string) => {
    try {
      setLoading(true);
      setSelectedCategory(categoryId);
      
      if (categoryId) {
        const categoryArticles = await knowledgeBaseService.getArticlesByCategory(
          categoryId, 
          true, 
          !user, 
          1, 
          20
        );
        setArticles(categoryArticles);
      } else {
        const allArticles = await knowledgeBaseService.getArticles({ 
          publicOnly: !user, 
          page: 1, 
          pageSize: 20 
        });
        setArticles(allArticles);
      }
      
      setCurrentView('browse');
    } catch (err) {
      console.error('Error filtering by category:', err);
      setError('Failed to filter articles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getArticleTypeLabel = (type: number): string => {
    const types = ['General', 'How To', 'Troubleshooting', 'FAQ', 'Policy', 'Training', 'Best Practice'];
    return types[type] || 'General';
  };

  const getArticleTypeColor = (type: number): string => {
    const colors = [
      'bg-gray-100 text-gray-800',
      'bg-blue-100 text-blue-800',
      'bg-red-100 text-red-800',
      'bg-purple-100 text-purple-800',
      'bg-yellow-100 text-yellow-800',
      'bg-green-100 text-green-800',
      'bg-indigo-100 text-indigo-800'
    ];
    return colors[type] || colors[0];
  };

  const renderStarRating = (rating: number, count: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push('★');
      } else if (i === fullStars && hasHalfStar) {
        stars.push('☆');
      } else {
        stars.push('☆');
      }
    }

    return (
      <div className="flex items-center space-x-1 text-sm">
        <span className="text-yellow-400">{stars.join('')}</span>
        <span className="text-gray-600">
          {rating.toFixed(1)} ({count} {count === 1 ? 'rating' : 'ratings'})
        </span>
      </div>
    );
  };

  const renderCategoryTree = (categories: KnowledgeCategory[], level = 0) => {
    return categories.map(category => (
      <div key={category.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        <button
          onClick={() => handleCategoryFilter(category.id)}
          className={`flex items-center justify-between w-full p-2 text-left hover:bg-gray-50 rounded ${
            selectedCategory === category.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
          }`}
        >
          <div className="flex items-center space-x-2">
            {category.icon && <span>{category.icon}</span>}
            <span className="font-medium">{category.name}</span>
          </div>
          <span className="text-sm text-gray-500">
            {category.articleCount}
          </span>
        </button>
        {category.subCategories.length > 0 && renderCategoryTree(category.subCategories, level + 1)}
      </div>
    ));
  };

  if (loading && articles?.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="large" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Knowledge Base</h1>
        <p className="text-gray-600">
          Find answers to common questions and browse our help articles
        </p>
      </div>

      {/* Search Section */}
      <Card className="mb-8">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search for articles, topics, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={KnowledgeSearchSortBy.Relevance}>Relevance</option>
                <option value={KnowledgeSearchSortBy.Title}>Title</option>
                <option value={KnowledgeSearchSortBy.CreatedDate}>Created Date</option>
                <option value={KnowledgeSearchSortBy.ViewCount}>View Count</option>
                <option value={KnowledgeSearchSortBy.Rating}>Rating</option>
              </select>
              <Button 
                onClick={handleSearch}
                disabled={searchLoading}
                variant="primary"
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setError(null);
              loadInitialData();
            }}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
              <div className="space-y-1">
                <button
                  onClick={() => handleCategoryFilter('')}
                  className={`flex items-center justify-between w-full p-2 text-left hover:bg-gray-50 rounded ${
                    selectedCategory === '' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span className="font-medium">All Articles</span>
                  <span className="text-sm text-gray-500">
                    {articles?.length || 0}
                  </span>
                </button>
                {renderCategoryTree(categories)}
              </div>
            </div>
          </Card>

          {/* Popular Articles */}
          {popularArticles?.length > 0 && (
            <Card className="mt-6">
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-4">Popular Articles</h3>
                <div className="space-y-3">
                  {popularArticles.slice(0, 5).map(article => (
                    <Link key={article.id} to={`/knowledge-base/articles/${article.id}`} className="block cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                      <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                        {article.title}
                      </h4>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">
                          {article.viewCount} views
                        </span>
                        <Badge className={getArticleTypeColor(article.type)}>
                          {getArticleTypeLabel(article.type)}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Featured Articles */}
          {currentView === 'browse' && featuredArticles?.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Featured Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {featuredArticles.slice(0, 4).map(article => (
                  <Link key={article.id} to={`/knowledge-base/articles/${article.id}`}>
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <Badge className="bg-yellow-100 text-yellow-800">Featured</Badge>
                          <Badge className={getArticleTypeColor(article.type)}>
                            {getArticleTypeLabel(article.type)}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                          {article.title}
                        </h3>
                        {article.summary && (
                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {article.summary}
                          </p>
                        )}
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>By {article.authorName}</span>
                          <span>{article.viewCount} views</span>
                        </div>
                        {article.ratingCount > 0 && (
                          <div className="mt-2">
                            {renderStarRating(article.rating, article.ratingCount)}
                          </div>
                        )}
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Articles List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {currentView === 'search' ? `Search Results` : 'All Articles'}
                {selectedCategory && (
                  <span className="text-base font-normal text-gray-600 ml-2">
                    in {categories.find(c => c.id === selectedCategory)?.name}
                  </span>
                )}
              </h2>
              <span className="text-sm text-gray-500">
                {articles?.length} {articles?.length === 1 ? 'article' : 'articles'}
              </span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loading size="large" />
              </div>
            ) : articles?.length === 0 ? (
              <Card>
                <div className="p-8 text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
                  <p className="text-gray-500">
                    {currentView === 'search' 
                      ? 'Try adjusting your search terms or browse by category.'
                      : 'No articles are available in this category.'}
                  </p>
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                {articles?.map(article => (
                  <Link key={article.id} to={`/knowledge-base/articles/${article.id}`}>
                    <Card className="hover:shadow-md transition-shadow cursor-pointer">
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge className={getArticleTypeColor(article.type)}>
                                {getArticleTypeLabel(article.type)}
                              </Badge>
                              {article.isFeatured && (
                                <Badge className="bg-yellow-100 text-yellow-800">Featured</Badge>
                              )}
                              {article.categoryName && (
                                <span className="text-sm text-gray-500">
                                  in {article.categoryName}
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {article.title}
                            </h3>
                            {article.summary && (
                              <p className="text-gray-600 mb-3 line-clamp-2">
                                {article.summary}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center space-x-4">
                            <span>By {article.authorName}</span>
                            <span>{article.viewCount} views</span>
                            <span>
                              {new Date(article.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        {article.ratingCount > 0 && (
                          <div className="mt-3">
                            {renderStarRating(article.rating, article.ratingCount)}
                          </div>
                        )}
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;